import distinctColors from "distinct-colors";
import tinycolor from "tinycolor2";
import { DEFAULT_BRAND } from "@/enums/brands";
import {THEME} from "@/enums/profile";
import {GetTheme} from "../common/general";

let colorCache: string[] = [];
let colorCacheKey: string = "";
let backgroundColorCache: {primary: string; secondary: string} = {
   primary: "",
   secondary: "",
};
let backgroundColorCacheKey: string = "";

const COLOR_DIFFERENCE = 15;

const getComplementColor = (
   base: tinycolor.Instance,
   complement: tinycolor.Instance | undefined
): string => {
   if (complement) {
      return complement.toHexString();
   }
   return base.isLight()
      ? base.darken(COLOR_DIFFERENCE).toHexString()
      : base.lighten(COLOR_DIFFERENCE).toHexString();
};

const computeColorsFromCSS = (
   primaryOverride?: string,
   secondaryOverride?: string,
   primaryCompOverride?: string,
   secondaryCompOverride?: string
): string[] | null => {
   if (typeof document === "undefined") {
      return null;
   }

   const htmlElement = document.querySelector("html");
   if (!htmlElement) {
      return null;
   }

   const computedStyle = getComputedStyle(htmlElement);

   const gailColorPrimary =
      primaryOverride ?? computedStyle.getPropertyValue("--gail-color-primary").trim();
   const gailColorPrimaryComp =
      primaryCompOverride ??
      computedStyle.getPropertyValue("--gail-color-primary-complement").trim();
   const gailColorSecondary =
      secondaryOverride ?? computedStyle.getPropertyValue("--gail-color-secondary").trim();
   const gailColorSecondaryComp =
      secondaryCompOverride ??
      computedStyle.getPropertyValue("--gail-color-secondary-complement").trim();

   const primary = tinycolor(`rgb ${gailColorPrimary}`);
   const primaryComp = gailColorPrimaryComp ? tinycolor(`rgb ${gailColorPrimaryComp}`) : undefined;
   const secondary = tinycolor(`rgb ${gailColorSecondary}`);
   const secondaryComp = gailColorSecondaryComp
      ? tinycolor(`rgb ${gailColorSecondaryComp}`)
      : undefined;

   return [
      primary.toHexString(),
      secondary.toHexString(),
      getComplementColor(primary, primaryComp),
      getComplementColor(secondary, secondaryComp),
   ];
};

const getBrandFromStorage = (): string => {
   try {
      if (globalThis.window !== undefined && globalThis.localStorage) {
         return (globalThis.localStorage.getItem("brand") as string) || DEFAULT_BRAND;
      }
   } catch {
      // Storage access may be blocked (e.g., incognito mode)
   }
   return DEFAULT_BRAND;
};

const isValidColorCount = (count?: number): count is number =>
   Boolean(count && count <= 4 && count > 0 && count !== 3);

export const GetColors = (
   count?: number,
   themeOverride?: "light" | "dark" | "auto",
   brandOverride?: string,
   primaryOverride?: string,
   secondaryOverride?: string,
   primaryCompOverride?: string,
   secondaryCompOverride?: string
): string[] => {
   if (!isValidColorCount(count)) {
      return count
         ? distinctColors({count, lightMin: 50, lightMax: 80}).map((color) => color.hex())
         : [];
   }

   const theme = themeOverride ?? GetTheme();
   const brand: string = brandOverride ?? getBrandFromStorage();
   const newKey = `${theme}-${brand}`;
   const hasOverrides = Boolean(
      themeOverride ||
         brandOverride ||
         primaryOverride ||
         secondaryOverride ||
         primaryCompOverride ||
         secondaryCompOverride
   );

   if (colorCacheKey === newKey && !hasOverrides) {
      return colorCache.slice(0, count);
   }

   const colors = computeColorsFromCSS(
      primaryOverride,
      secondaryOverride,
      primaryCompOverride,
      secondaryCompOverride
   );
   if (!colors) {
      return [];
   }

   if (!hasOverrides) {
      colorCache = colors;
      colorCacheKey = newKey;
   }

   return colors.slice(0, count);
};

const getEffectiveTheme = (theme: THEME): THEME.LIGHT | THEME.DARK => {
   if (theme === THEME.SYSTEM) {
      // Detect system preference
      if (globalThis.window?.matchMedia?.("(prefers-color-scheme: dark)").matches) {
         return THEME.DARK;
      }
      return THEME.LIGHT;
   }
   return theme === THEME.DARK ? THEME.DARK : THEME.LIGHT;
};

const AdjustToBackgroundColor = (color: tinycolor.Instance, theme: THEME): string => {
   const effectiveTheme = getEffectiveTheme(theme);

   if (effectiveTheme === THEME.LIGHT) {
      const adjustment = 80 - (color.getBrightness() / 255) * 100;

      return adjustment < 0 ? color.toHexString() : color.brighten(adjustment).toHexString();
   } else {
      const adjustment = (color.getBrightness() / 255) * 100 - 20;

      return adjustment <= 0 ? color.toHexString() : color.darken(adjustment).toHexString();
   }
};

export const GetBackgroundColors = (
   themeOverride?: THEME,
   brandOverride?: string,
   primaryOverride?: string,
   secondaryOverride?: string
): {
   primary: string;
   secondary: string;
} | null => {
   const theme: THEME = themeOverride ?? GetTheme();
   const brand: string = brandOverride ?? getBrandFromStorage();

   //Use Primary and Secondary Colors and then a variation of those colors to fill out 4
   const newKey = `${theme}-${brand}`;
   const hasOverrides = themeOverride || brandOverride || primaryOverride || secondaryOverride;

   if (backgroundColorCacheKey === newKey && !hasOverrides) {
      return backgroundColorCache;
   }

   if (brand === "movistar") {
      backgroundColorCache = {
         primary: theme === THEME.LIGHT ? "#D2E2FF" : tinycolor("#D2E2FF").darken(50).toHexString(),
         secondary:
            theme === THEME.LIGHT ? "#EEF4FF" : tinycolor("#EEF4FF").darken(50).toHexString(),
      };
      backgroundColorCacheKey = newKey;
      return backgroundColorCache;
   }

   if (typeof document === "undefined") {
      return null;
   }

   const htmlElement = document.querySelector("html");

   if (htmlElement) {
      const computedStyle = getComputedStyle(htmlElement);

      const gailColorPrimary =
         primaryOverride ?? computedStyle.getPropertyValue("--gail-color-primary").trim();
      const gailColorSecondary =
         secondaryOverride ?? computedStyle.getPropertyValue("--gail-color-secondary").trim();

      const primary = tinycolor(`rgb ${gailColorPrimary}`);
      const secondary = tinycolor(`rgb ${gailColorSecondary}`);

      const colors = {
         primary: AdjustToBackgroundColor(primary, theme),
         secondary: AdjustToBackgroundColor(secondary, theme),
      };

      // Only update cache when no overrides are used to avoid cache corruption
      if (!hasOverrides) {
         backgroundColorCache = colors;
         backgroundColorCacheKey = newKey;
      }

      return colors;
   }

   return null;
};

export const GetBackgroundColor = (
   brandOverride?: string,
   primaryOverride?: string,
   themeOverride?: THEME
): {
   primary: string;
} | null => {
   const brand: string = brandOverride ?? getBrandFromStorage();
   const theme: THEME = themeOverride ?? GetTheme();

   if (brand === "movistar") {
      return {
         primary: theme === THEME.LIGHT ? "#D2E2FF" : tinycolor("#D2E2FF").darken(50).toHexString(),
      };
   }

   if (typeof document === "undefined") {
      return null;
   }

   const htmlElement = document.querySelector("html");

   if (htmlElement) {
      const computedStyle = getComputedStyle(htmlElement);

      const gailColorPrimary =
         primaryOverride ?? computedStyle.getPropertyValue("--gail-color-primary").trim();

      const primary = tinycolor(`rgb ${gailColorPrimary}`);

      return {
         primary: primary.toHexString(),
      };
   }

   return null;
};

export const GetFontColors = (colors: {
   primary: string;
   secondary: string;
}): {
   primary: string;
   secondary: string;
} => {
   const primary = tinycolor(colors.primary);
   const secondary = tinycolor(colors.secondary);

   return {
      primary: primary.isLight() ? "#000000" : "#FFFFFF",
      secondary: secondary.isLight() ? "#000000" : "#FFFFFF",
   };
};

export const HexToRgb = (hex: string) => {
   // Remove leading '#' if present
   const cleanedHex = hex.replace(/^#/, "");
   // Only allow 3 or 6 digit hex
   if (!/^([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(cleanedHex)) {
      return hex; // fallback to original if not a valid hex
   }
   let r: number, g: number, b: number;
   if (cleanedHex.length === 3) {
      r = Number.parseInt(cleanedHex[0] + cleanedHex[0], 16);
      g = Number.parseInt(cleanedHex[1] + cleanedHex[1], 16);
      b = Number.parseInt(cleanedHex[2] + cleanedHex[2], 16);
   } else {
      r = Number.parseInt(cleanedHex.substring(0, 2), 16);
      g = Number.parseInt(cleanedHex.substring(2, 4), 16);
      b = Number.parseInt(cleanedHex.substring(4, 6), 16);
   }
   return `${r} ${g} ${b}`;
};

export const RgbToHex = (rgb: string) => {
   const parts = rgb.split(" ").map(Number);
   const [r, g, b] = parts;

   // Validate that we have 3 valid numbers in the 0-255 range
   if (
      parts.length !== 3 ||
      Number.isNaN(r) ||
      Number.isNaN(g) ||
      Number.isNaN(b) ||
      r < 0 ||
      r > 255 ||
      g < 0 ||
      g > 255 ||
      b < 0 ||
      b > 255
   ) {
      return "#000000"; // Fallback to black for invalid input
   }

   return `#${Math.round(r).toString(16).padStart(2, "0")}${Math.round(g)
      .toString(16)
      .padStart(2, "0")}${Math.round(b).toString(16).padStart(2, "0")}`;
};

export const GetHexFromColor = (color: string) => {
   const isHex = color.startsWith("#");
   if (isHex) {
      return color;
   }
   return RgbToHex(color);
};

/**
 * Get the primary color from CSS variables as an RGB triplet string
 * Returns the RGB triplet from --gail-color-primary (e.g., "37 99 235")
 */
export const GetPrimaryColorRgb = (): string => {
   if (typeof document === "undefined") {
      return "37 99 235"; // Default fallback (blue)
   }

   const rgb = getComputedStyle(document.documentElement)
      .getPropertyValue("--gail-color-primary")
      .trim();

   return rgb || "37 99 235";
};

/**
 * Get the primary color as a hex string
 */
export const GetPrimaryColorHex = (): string => {
   return RgbToHex(GetPrimaryColorRgb());
};

/**
 * Generate a palette of colors based on the primary color
 * Uses tinycolor2 for proper color manipulation
 * Returns an array of hex colors with variations
 */
const adjustColor = (primary: tinycolor.Instance, adjustment: number): tinycolor.Instance => {
   if (adjustment > 0) {
      return primary.clone().lighten(adjustment);
   }
   if (adjustment < 0) {
      return primary.clone().darken(Math.abs(adjustment));
   }
   return primary.clone();
};

export const GeneratePrimaryColorPalette = (
   count: number = 6,
   theme: THEME = THEME.LIGHT
): string[] => {
   const primaryRgb = GetPrimaryColorRgb();
   const primary = tinycolor(`rgb(${primaryRgb.split(" ").join(",")})`);
   const colors: string[] = [];

   // Wider spread for better distinction between colors
   const adjustments =
      theme === THEME.DARK
         ? [45, 25, 5, -15, -30, -45] // Lighter variations for dark mode visibility
         : [35, 10, -15, -35, -50, -60]; // Light to dark variations for light mode

   for (let i = 0; i < count; i++) {
      const adjustment = adjustments[i % adjustments.length];
      colors.push(adjustColor(primary, adjustment).toHexString());
   }

   return colors;
};

/**
 * Get the primary color adjusted for the current theme
 * Lightens the color in dark mode for better visibility
 */
export const GetPrimaryColorForTheme = (theme: THEME): string => {
   const primaryRgb = GetPrimaryColorRgb();
   const primary = tinycolor(`rgb(${primaryRgb.split(" ").join(",")})`);

   if (theme === THEME.DARK) {
      // Lighten for better visibility in dark mode
      return primary.lighten(15).toHexString();
   }

   return primary.toHexString();
};
